'use client'

import { ChevronDownIcon } from '@heroicons/react/24/solid'
import cartIcon from '@public/images/cart.svg'
import Image from 'next/image'

import { Button } from '@src/components/Button/Button'
import { Container } from '@src/components/Container/Container'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useCart } from '@src/hooks/useCart'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { getCartQuantity } from '@src/utils/cart.util'
import { cn } from '@src/utils/tailwind'

import { defaultTopNavProps } from './TopNav.defaults'
import { type TopNavProps } from './TopNav.props'

export const TopNavDesktop = (props: TopNavProps = defaultTopNavProps) => {
  const { t } = useLocale('common')
  const { cart } = useCart()
  const quantity = getCartQuantity(cart)
  const trackSelectNavCta = useTrackSelectNavCta()

  const renderMenus = (menus: TopNavProps['menus']) => {
    return menus
      ?.filter((menu) => Boolean(menu.title))
      ?.map((menu, index) => {
        if (!menu?.hasDropdown) {
          return (
            <LocalizedLink
              onClick={() => {
                trackSelectNavCta({ 'cta url': menu.href })
              }}
              key={menu?.title}
              className="block cursor-pointer py-4 transition-all hover:text-bodi-white"
              href={menu.href ?? ''}
              rel={menu.newTab ? 'noopener noreferrer' : ''}
              target={menu.newTab ? '_blank' : '_self'}
            >
              {menu.title}
            </LocalizedLink>
          )
        }

        return (
          <div
            data-testid={`nav-menu-${index}`}
            key={menu.title}
            className="group/link relative flex cursor-pointer items-center gap-2 py-4 transition-all hover:text-bodi-white"
          >
            {menu.title}
            <ChevronDownIcon className="size-5 transition-all group-hover/link:rotate-180" />
            <div className="absolute left-1/2 top-14 z-20 hidden h-auto w-72 -translate-x-1/2 overflow-hidden rounded-[14px] border border-bodi-gray-dark bg-bodi-black-text text-body-md text-bodi-white shadow-lg group-hover/link:block">
              <div className="flex flex-col p-3">
                {menu?.items
                  ?.filter((item) => Boolean(item.title))
                  ?.map((item, itemIdx) => (
                    <LocalizedLink
                      data-testid={`nav-menu-${index}-item-${itemIdx}`}
                      href={item.href ?? ''}
                      key={item.title}
                      className="rounded-[4px] p-3 transition-all hover:bg-bodi-gray-medium"
                      rel={item.newTab ? 'noopener noreferrer' : ''}
                      target={item.newTab ? '_blank' : '_self'}
                    >
                      {item.title}
                    </LocalizedLink>
                  ))}
              </div>
              {menu.hasFeaturedItemDesktop && menu?.featuredItemDesktop?.title && (
                <LocalizedLink
                  data-testid="menu-featured-item-desktop"
                  onClick={() => {
                    trackSelectNavCta({ 'cta url': menu.featuredItemDesktop?.href })
                  }}
                  href={menu?.featuredItemDesktop?.href ?? ''}
                  className="block bg-bodi-green-bright-500 p-4 text-center font-semibold text-bodi-black-text transition-all hover:bg-bodi-green-bright-400"
                  rel={menu?.featuredItemDesktop?.newTab ? 'noopener noreferrer' : ''}
                  target={menu?.featuredItemDesktop?.newTab ? '_blank' : '_self'}
                >
                  {menu?.featuredItemDesktop?.title}
                </LocalizedLink>
              )}
            </div>
          </div>
        )
      })
  }

  return (
    <nav className="hidden h-20 w-full bg-bodi-black-text font-medium text-bodi-gray-butters lg:block">
      <Container className="h-full *:h-full">
        <div className="flex h-full items-center *:flex *:gap-8">
          <div className="flex items-center">
            {props?.logo?.src && (
              <LocalizedLink
                onClick={() => {
                  trackSelectNavCta({ 'cta url': props?.logo?.href })
                }}
                href={props?.logo?.href ?? ''}
                className="block"
              >
                <Image
                  src={props.logo.src}
                  width={props?.logo?.desktopWidth}
                  height={props?.logo?.desktopHeight}
                  alt={props?.logo?.altText ?? ''}
                />
              </LocalizedLink>
            )}
            {renderMenus(props.menus)}
          </div>
          <div className="ml-auto flex items-center">
            <LocalizedLink data-testid="cartBtn" href="/cart" className="relative mt-0.5">
              <Image src={cartIcon} width={24} height={24} alt={t('view-cart')} />
              {quantity ? (
                <div
                  className={cn(
                    'absolute left-full top-0 -mt-3 ml-[-18px] flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full border-2 border-bodi-black-text bg-bodi-green-bright-500 text-body-xs font-semibold text-bodi-black',
                    { 'px-1.5': quantity > 99 }
                  )}
                >
                  {quantity > 999 ? '999' : quantity}
                </div>
              ) : null}
            </LocalizedLink>
            <LocalizedLink
              onClick={() => {
                trackSelectNavCta({ 'cta url': props?.links?.logIn?.href })
              }}
              data-testid="logInBtn"
              href={props.links?.logIn?.href ?? ''}
              className="flex cursor-pointer gap-2 hover:text-bodi-white"
              rel={props.links?.logIn?.newTab ? 'noopener noreferrer' : ''}
              target={props.links?.logIn?.newTab ? '_blank' : '_self'}
            >
              {props.links?.logIn?.title}
            </LocalizedLink>
            <LocalizedLink
              onClick={() => {
                trackSelectNavCta({ 'cta url': props?.links?.signUp?.href })
              }}
              data-testid="signUpBtn"
              href={props.links?.signUp?.href ?? ''}
              rel={props.links?.signUp?.newTab ? 'noopener noreferrer' : ''}
              target={props.links?.signUp?.newTab ? '_blank' : '_self'}
            >
              <Button className="[&>div]:px-8" size="small">
                {props.links?.signUp?.title}
              </Button>
            </LocalizedLink>
          </div>
        </div>
      </Container>
    </nav>
  )
}
